import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import { Panel } from '../../components/layout/Panel';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { ReferencedWorkDTO, WorkProjectDTO, WorkProjectOtherDTO } from '../../../../api/core';
import { getDefaultWorkProject, getDefaultWorkProjectOther } from '../../../../Settings/emptyModels';
import React, { ReactElement, ReactNode } from 'react';
import { InputGroup } from '../../components/layout/InputGroup';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { IntlShape, useIntl } from 'react-intl';
import { LabelledProjectSuggester } from '../../../components/form/LabelledProjectSuggester';
import {
  referencedProjectToSelectedProject,
  selectedProjectToReferencedProject,
} from '../../components/suggester/ProjectSuggester';

export const ProjectsPage = (props: Props): ReactElement => {
  const intl: IntlShape = useIntl();
  return <Block title={`work.header.projects`}>
    <PermissionGate always={false} entity={'work_generic'} field="work_projects">
      {(checkPermission: CheckPermissionFunction) =>
        <Panel key={'projects'} className={'col-12'} title={'work.attr.workProjects'}>
          <MultiInputControls<WorkProjectDTO>
            canEdit={checkPermission().canWrite}
            lines={props.modifiedModel.workProjects}
            addNew={(rows: WorkProjectDTO[]): number => rows.push(getDefaultWorkProject())}
            onChange={(projects: WorkProjectDTO[]): void => props.setField('workProjects', projects)}
            renderLines={(parent: WorkProjectDTO, onLineChange: (newValues: WorkProjectDTO) => void, actions: Actions, idx: number): ReactNode => {
              return <InputGroup
                delete={{
                  action: actions.delete,
                  disabled: !checkPermission().canWrite,
                }}
              >
                <LabelledProjectSuggester
                  id={`projects-${idx}-project`}
                  autoFocus={props.autoFocus}
                  value={(parent.project ? [parent.project] : []).map(referencedProjectToSelectedProject)}
                  validation={props.validation?.root?.fields?.work_projects.children[idx].fields?.project}
                  permissions={checkPermission('project')}
                  onChange={(projects: ReferencedWorkDTO[]): void => onLineChange({
                    ...parent,
                    project: projects.length > 0 ? projects.map(selectedProjectToReferencedProject).pop() : undefined,
                  })}
                  label={'work.attr.workProject.attr.project'}
                  multiple={false}
                />
                <LabelledDropdown
                  id={`projects-${idx}-role`}
                  value={parent.role}
                  permissions={checkPermission('role')}
                  validation={props.validation?.root?.fields?.work_projects.children[idx].fields?.role}
                  onChange={(role: string): void => onLineChange({...parent, role})}
                  label={'work.attr.workProject.attr.role'}
                  options={getVocabularyOptions(intl.locale, 'corporation_role_list')}
                />
              </InputGroup>;
            }}
          />
        </Panel>
      }
    </PermissionGate>

    <PermissionGate always={false} entity={'work_generic'} field="work_project_others">
      {(checkPermission: CheckPermissionFunction) =>
        <Panel key={'projectOthers'} className={'col-12'}
               title={'work.attr.workProjectOthers'}>
          <MultiInputControls<WorkProjectOtherDTO>
            canEdit={checkPermission().canWrite}
            lines={props.modifiedModel.workProjectOthers}
            addNew={(rows: WorkProjectOtherDTO[]): number => rows.push(getDefaultWorkProjectOther())}
            onChange={(projects: WorkProjectOtherDTO[]): void => props.setField('workProjectOthers', projects)}
            renderLines={(parent: WorkProjectOtherDTO, onLineChange: (newValues: WorkProjectOtherDTO) => void, actions: Actions, idx: number): ReactNode => {
              return <InputGroup
                delete={{
                  action: actions.delete,
                  disabled: !checkPermission().canWrite,
                }}
              >
                <LabelledInputText
                  id={`projectOther-${idx}-name`}
                  autoFocus={props.autoFocus}
                  value={parent.name || ''}
                  validation={props.validation?.root?.fields?.work_project_others.children[idx].fields?.name}
                  permissions={checkPermission('name')}
                  onChange={(name: string): void => onLineChange({...parent, name})}
                  label={'work.attr.workProject.attr.otherName'}
                />
                <LabelledDropdown
                  id={`projectOther-${idx}-role`}
                  value={parent.role}
                  permissions={checkPermission('role')}
                  validation={props.validation?.root?.fields?.work_project_others.children[idx].fields?.role}
                  onChange={(role: string): void => onLineChange({...parent, role})}
                  label={'work.attr.workProject.attr.role'}
                  options={getVocabularyOptions(intl.locale, 'corporation_role_list')}
                />
              </InputGroup>;
            }}
          />
        </Panel>
      }
    </PermissionGate>
  </Block>;
};
